import { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { playerAuthService, LoginPlayerData, RegisterPlayerData, PlayerRole } from '@/lib/services/player-auth.service';
import { useStore } from './StoreContext';

interface Player {
  id: string;
  name: string;
  email: string;
  nick: string;
  credits: number;
  role: PlayerRole | null;
}

interface PlayerAuthContextType {
  player: Player | null;
  loading: boolean;
  login: (data: LoginPlayerData) => Promise<void>;
  register: (data: RegisterPlayerData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const PlayerAuthContext = createContext<PlayerAuthContextType | undefined>(undefined);

interface PlayerAuthProviderProps {
  children: ReactNode;
}

/**
 * Thin wrapper that delegates auth state to AuthContext.
 * Exists only to preserve the PlayerRole type expected by forum components.
 */
export function PlayerAuthProvider({ children }: PlayerAuthProviderProps) {
  const { user, isLoading, login: authLogin, register: authRegister, logout: authLogout } = useAuth();
  const { store } = useStore();

  const player: Player | null = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        nick: user.nick,
        credits: user.credits,
        role: null,
      }
    : null;

  const login = async (data: LoginPlayerData) => {
    // Nick na loja de jogo; e-mail na loja de produtos digitais.
    const result = await authLogin('nick' in data ? data.nick : data.email, data.password);
    if (!result.success) throw new Error(result.error || 'Login failed');
  };

  const register = async (data: RegisterPlayerData) => {
    if (!store) throw new Error('Store not loaded');
    await playerAuthService.register(store.id, data);
    await login(data.hytaleNick ? { nick: data.hytaleNick, password: data.password } : { email: data.email, password: data.password });
  };

  return (
    <PlayerAuthContext.Provider
      value={{
        player,
        loading: isLoading,
        login,
        register,
        logout: authLogout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </PlayerAuthContext.Provider>
  );
}

export function usePlayerAuth() {
  const context = useContext(PlayerAuthContext);
  if (!context) {
    throw new Error('usePlayerAuth must be used within PlayerAuthProvider');
  }
  return context;
}

export default PlayerAuthContext;
